<template>
  <div
    v-if="show"
    class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 px-4"
    @click.self="close"
  >
    <div
      class="
        bg-gray-900 border border-gray-600 rounded-lg w-full max-w-5xl
        text-gray-100 shadow-2xl flex flex-col overflow-hidden
      "
      style="max-height: 50vh;"
    >
      <div class="p-4 border-b border-gray-700 space-y-2">
        <div class="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
          <div>
            <h2 class="text-2xl font-semibold">
              Sub-domains for {{ domainLabel }}
            </h2>
            <p class="text-sm text-gray-400">
              {{ subdomains.length }} {{ subdomains.length === 1 ? 'entry' : 'entries' }}
            </p>
          </div>
          <div class="flex flex-wrap gap-2 md:justify-end">
            <Button class="px-3 py-1 text-sm" @onClick="exportJson">
              Export JSON
            </Button>
            <Button class="px-3 py-1 text-sm" @onClick="exportCsv">
              Export CSV
            </Button>
            <Button class="px-3 py-1 text-sm" @onClick="exportTxt">
              Export TXT
            </Button>
            <Button type="secondary" class="px-3 py-1 text-sm" @onClick="close">
              Close
            </Button>
          </div>
        </div>
      </div>
      <div class="flex-1 overflow-y-auto overflow-x-auto p-4 pr-2 min-h-0">
        <div
          v-if="subdomains.length"
          class="
            grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4
            xl:grid-cols-5 gap-y-1 gap-x-3 font-mono text-xs
          "
        >
          <div
            v-for="(sd, i) in subdomains"
            :key="i"
            class="whitespace-nowrap"
          >
            {{ sd }}
          </div>
        </div>
        <p v-else class="text-gray-400 text-sm">
          No sub-domains discovered.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import Button from '@/components/Button.vue';

export default {
  name: 'SubdomainsModal',
  components: { Button },
  props: {
    executiveReport: { type: Object, required: true },
    reportId: { type: String, required: true },
    show: { type: Boolean, default: false },
  },
  computed: {
    subdomains() {
      const set = new Set();
      if (!this.executiveReport
        || !this.executiveReport.aggregatedResult
        || !Array.isArray(this.executiveReport.aggregatedResult.ips)) {
        return [];
      }
      this.executiveReport.aggregatedResult.ips.forEach((host = {}) => {
        if (Array.isArray(host.subdomains)) {
          host.subdomains.forEach((sd) => {
            if (sd) set.add(sd);
          });
        }
      });
      return Array.from(set).sort();
    },
    domainLabel() {
      if (this.executiveReport
        && Array.isArray(this.executiveReport.reportInput)
        && this.executiveReport.reportInput.length) {
        return this.executiveReport.reportInput[0];
      }
      if (this.executiveReport && this.executiveReport.reportInput) {
        return this.executiveReport.reportInput;
      }
      return `Report ${this.reportId}`;
    },
  },
  methods: {
    close() {
      this.$emit('close');
    },
    exportJson() {
      const blob = new Blob([JSON.stringify(this.subdomains, null, 2)], {
        type: 'application/json',
      });
      this.downloadFile(blob, this.buildFilename('json'));
    },
    exportCsv() {
      const rows = this.subdomains.map((s) => s);
      const csv = ['subdomain'].concat(rows).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      this.downloadFile(blob, this.buildFilename('csv'));
    },
    exportTxt() {
      const blob = new Blob([this.subdomains.join('\n')], { type: 'text/plain' });
      this.downloadFile(blob, this.buildFilename('txt'));
    },
    buildFilename(extension) {
      const safeDomain = (this.domainLabel || `report-${this.reportId}`)
        .toString()
        .trim()
        .replace(/[^a-zA-Z0-9.-]+/g, '-');
      return `${safeDomain}-subdomains.${extension}`;
    },
    downloadFile(blob, filename) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    handleKeydown(event) {
      if (event.key === 'Escape' && this.show) {
        this.close();
      }
    },
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeydown);
  },
};
</script>
